import React from "react";
import CarbonCard from "../common/CarbonCard";
import TwoColumnBarGraph from "../common/TwoColumnBarGraph";
import VerticalBarChart from "../common/VerticalBarChart";
import { ReactComponent as Waterfootprint } from "../../../../../assets/svgs/waterfootprints.svg";
import { ReactComponent as Enviromental1 } from "../../../../../assets/svgs/Enviromental11.svg";
import { ReactComponent as Env1 } from "../../../../../assets/svgs/Env01.svg";
import { ReactComponent as Env2 } from "../../../../../assets/svgs/Env02.svg";
import { ReactComponent as Env3 } from "../../../../../assets/svgs/Env03.svg";
import { ReactComponent as Waste } from "../../../../../assets/svgs/Wastes.svg";
import HorizontalBarCharts from "../common/HorizontalBarCharts";
import HorizontalBarChart from "../common/HorizontalBarChart";
import SimplePieChart from "../common/SimplePieChart";
import ProgressCharts from "../common/ProgressCharts";
import { ReactComponent as EmployeeTurnoverRateImage } from "../../../../../assets/svgs/Recyclables.svg";
import InfoCard from "../common/InfoCard";
import { ReactComponent as Back } from "../../../../../assets/svgs/back-button.svg";
import TextStyles from "../../../../../components/Elements/TextStyles/TextStyles";
import { THEME_COLORS } from "../../../../../color";
import TimeFilterBar from "../TimeFilterBar";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PermissionForm from "../../../../Auth/PermissionForm";
import { useState, useEffect } from "react";
import { getApi } from "../../../../../api/api_instance";
import { API_URLS } from "../../../../../api/apiUrls";

const EmissionDashboard = () => {
  const [showPermission, setPermission] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeDashboard, setActiveDashboard] = useState(() => {
    return sessionStorage.getItem("hideDummy") === "true" ? "live" : "dummy";
  });
  const [loading, setLoading] = useState(() => {
    return sessionStorage.getItem("hideDummy") === "true";
  });
  const { user, selectedGroupMember } = useSelector((state) => state.auth);
  const [yearFilter, setYearFilter] = useState(() =>
    user?.reportingYearType === "calendar" ? "2026" : "2026-2027",
  );
  const isPermissionCompleted = user?.isCompleted;

  const navigate = useNavigate();

  const selectedMemberId =
    selectedGroupMember === "string"
      ? selectedGroupMember
      : selectedGroupMember?.clientId ||
        selectedGroupMember?._id ||
        selectedGroupMember?.client_id?._id ||
        selectedGroupMember?.client_id;

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (activeDashboard !== "live") {
        setLoading(false);
        setDashboardData(null);
        return;
      }

      try {
        setLoading(true);
        let apiUrl;

        if (selectedMemberId) {
          apiUrl = API_URLS.GET_GROUP_MEMBER_DASHBOARD(
            "environmental_impact",
            selectedMemberId,
          );
        } else {
          apiUrl = API_URLS.GET_SDG_DASHBOARD("environmental_impact");
        }

        const queryParams = new URLSearchParams();
        if (yearFilter) queryParams.append("year", yearFilter);
        const queryString = queryParams.toString()
          ? `?${queryParams.toString()}`
          : "";
        apiUrl += queryString;

        console.log("Environmental Impact API Hit:", apiUrl);
        const res = await getApi({
          apiUrl: apiUrl,
        });
        console.log("Environmental Impact Response:", res);
        if (res?.data) {
          const data = res.data.dashboard ? res.data.dashboard : res.data;
          setDashboardData(data);
        }
      } catch (error) {
        console.error("Error fetching Environmental Impact data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [selectedMemberId, activeDashboard, yearFilter]);

  useEffect(() => {
    if (!isPermissionCompleted) {
      setPermission(true);
    }
  }, [isPermissionCompleted]);

  const getComponent = (id) =>
    activeDashboard === "live"
      ? dashboardData?.components?.find((c) => c.componentId === id)
      : null;

  const treesVal = getComponent("env_trees_lost")?.data?.value;
  const treesDisplay =
    activeDashboard === "live"
      ? treesVal !== undefined
        ? treesVal.toLocaleString()
        : "0"
      : "700257";

  const coalVal = getComponent("env_coal_burnt")?.data?.value;
  const coalDisplay =
    activeDashboard === "live"
      ? coalVal !== undefined
        ? coalVal.toLocaleString()
        : "0"
      : "6231.4";

  const iceVal = getComponent("env_ice_melt")?.data?.value;
  const iceDisplay =
    activeDashboard === "live"
      ? iceVal !== undefined
        ? iceVal.toLocaleString()
        : "0"
      : "44697.3";

  const waterComp = getComponent("env_water_footprint");
  const groundwaterVal = waterComp?.data?.groundwater || 0;
  const surfaceWaterVal = waterComp?.data?.surfaceWater || 0;
  const thirdPartyVal = waterComp?.data?.thirdParty || 0;
  const seawaterVal = waterComp?.data?.seawaterDesalinated || 0;
  const totalWaterVal = waterComp?.data?.totalConsumption || 0;

  const rawWaterData =
    activeDashboard === "live"
      ? [surfaceWaterVal, groundwaterVal, thirdPartyVal, seawaterVal]
      : [21000, 47300, 29500, 7500];

  const maxWaterVal = Math.max(...rawWaterData, 1);
  const allWaterZero = rawWaterData.every((val) => val === 0);
  const waterChartData = rawWaterData.map((val) => {
    if (val === 0) {
      return allWaterZero ? 1.5 : maxWaterVal * 0.015;
    }
    return val;
  });

  const waterTotalDisplay =
    activeDashboard === "live"
      ? `${totalWaterVal.toLocaleString()} Kiloliters`
      : "40,000 Kiloliters";

  const activityComp = getComponent("env_water_by_activity");
  const activityData =
    activeDashboard === "live"
      ? activityComp?.data &&
        Array.isArray(activityComp.data) &&
        activityComp.data.length > 0
        ? activityComp.data.map((a) => a.value || 0)
        : [0, 0, 0, 0]
      : [13500, 62000, 18912, 17000];

  const activityLabels =
    activeDashboard === "live"
      ? activityComp?.data &&
        Array.isArray(activityComp.data) &&
        activityComp.data.length > 0
        ? activityComp.data.map((a) => a.label || "Other")
        : ["Domestic use", "Industrial processes", "Agriculture", "Irrigations"]
      : ["Domestic use", "Industrial processes", "Agriculture", "Irrigations"];

  const comparisonComp = getComponent("env_water_comparison");
  const comparisonActual = comparisonComp?.data?.actual || 0;
  const comparisonPct = comparisonComp?.data?.percentage || 0;
  const comparisonStandard = comparisonComp?.data?.standard || 45;

  const comparisonValue = activeDashboard === "live" ? comparisonPct : 70;
  const comparisonPrimaryLegend =
    activeDashboard === "live"
      ? `Water usage per employee in operational hour (${comparisonActual} litres)`
      : "Water usage per employee in operational hour (31.5 litres)";
  const comparisonSecondaryLegend =
    activeDashboard === "live"
      ? `NBC BIS standards (${comparisonStandard} litres)`
      : "NBC BIS standards (45 litres)";

  const intensityComp = getComponent("env_water_intensity");
  const intensityItems =
    activeDashboard === "live"
      ? intensityComp?.data && Array.isArray(intensityComp.data)
        ? intensityComp.data.map((item) => {
            let label = item.label || "";
            if (
              label.toLowerCase().includes("output") ||
              label.toLowerCase().includes("product")
            ) {
              label = "Cubic meters per product";
            } else if (
              label.toLowerCase().includes("turnover") ||
              label.toLowerCase().includes("revenue")
            ) {
              label = "Litres per rupee revenue";
            } else if (
              label.toLowerCase().includes("workforce") ||
              label.toLowerCase().includes("employee")
            ) {
              label = "Cubic meters per employee in operational hour";
            }
            return {
              value: item.value !== undefined ? item.value : 0,
              label: label,
            };
          })
        : []
      : [
          { value: 557.06, label: "Cubic meters per product" },
          { value: 0.03, label: "Litres per rupee revenue" },
          {
            value: 15.87,
            label: "Cubic meters per employee in operational hour",
          },
        ];

  const WASTE_LABEL_MAP = {
    cd_waste: ["C&D", "Waste"],
    c_and_d_waste: ["C&D", "Waste"],
    c_d_waste: ["C&D", "Waste"],
    hazardous_waste: ["Hazardous", "Waste"],
    biodegradable_waste: ["Biodegradable", "waste"],
    non_biodegradable_waste: ["Non-", "Biodegradable", "waste"],
    electronic_waste: ["Electronic", "Waste"],
    recyclable_waste: ["Recyclable", "Waste"],
  };

  const formatWasteLabel = (label) => {
    if (!label) return ["Other"];
    const labelStr =
      typeof label === "string"
        ? label
        : String(label?.label || label?.name || label);
    const cleanLabel = labelStr.trim();
    const mapped =
      WASTE_LABEL_MAP[cleanLabel] || WASTE_LABEL_MAP[cleanLabel.toLowerCase()];
    if (mapped) return mapped;

    if (cleanLabel.includes(" ")) {
      const words = cleanLabel.split(" ");
      if (words.length === 2) return words;
      return [words[0], words.slice(1).join(" ")];
    }
    return [cleanLabel];
  };

  const wasteComp = getComponent("env_waste_disposal");
  const wasteCategories =
    activeDashboard === "live"
      ? wasteComp?.data &&
        Array.isArray(wasteComp.data) &&
        wasteComp.data.length > 0
        ? wasteComp.data.map((w) =>
            formatWasteLabel(
              w.name || w.scope3_Waste_generated_in_operations || "Other",
            ),
          )
        : [
            ["C&D", "Waste"],
            ["Hazardous", "Waste"],
            ["Biodegradable", "waste"],
            ["Non-", "Biodegradable", "waste"],
            ["Electronic", "Waste"],
          ]
      : [
          ["C&D", "Waste"],
          ["Hazardous", "Waste"],
          ["Biodegradable", "waste"],
          ["Non-", "Biodegradable", "waste"],
          ["Electronic", "Waste"],
        ];

  const wasteGeneratedData =
    activeDashboard === "live"
      ? wasteComp?.data &&
        Array.isArray(wasteComp.data) &&
        wasteComp.data.length > 0
        ? wasteComp.data.map((w) =>
            w.generated !== undefined
              ? w.generated
              : w.biodegradable_waste_multiselect !== undefined
                ? w.biodegradable_waste_multiselect
                : 0,
          )
        : [0, 0, 0, 0, 0]
      : [210, 58.5, 125, 310, 22.4];

  const wasteRecycledData =
    activeDashboard === "live"
      ? wasteComp?.data &&
        Array.isArray(wasteComp.data) &&
        wasteComp.data.length > 0
        ? wasteComp.data.map((w) =>
            w.recycled !== undefined
              ? w.recycled
              : w.recycled_waste_multiselect !== undefined
                ? w.recycled_waste_multiselect
                : 0,
          )
        : [0, 0, 0, 0, 0]
      : [168, 31.2, 97, 145, 22];

  const allWasteValues = [...wasteGeneratedData, ...wasteRecycledData];
  const maxWasteValue = Math.max(...allWasteValues, 0);
  let calculatedWasteMax = 500;
  if (maxWasteValue > 0) {
    const valWithHeadroom = maxWasteValue * 1.15;
    if (maxWasteValue >= 100) {
      calculatedWasteMax = Math.ceil(valWithHeadroom / 100) * 100;
    } else if (maxWasteValue >= 10) {
      calculatedWasteMax = Math.ceil(valWithHeadroom / 10) * 10;
    } else {
      calculatedWasteMax = Math.ceil(valWithHeadroom);
    }
  }

  const recyclableComp = getComponent("env_recyclable");
  const recyclableData =
    activeDashboard === "live"
      ? recyclableComp?.data
        ? [
            recyclableComp.data.recyclable_waste !== undefined
              ? recyclableComp.data.recyclable_waste
              : recyclableComp.data.recyclableWaste || 0,
            recyclableComp.data.waste_sent_recycling !== undefined
              ? recyclableComp.data.waste_sent_recycling
              : recyclableComp.data.wasteSentRecycling || 0,
          ]
        : [0, 0]
      : [725.9, 463.2];

  const fundComp = getComponent("env_fund_allocation");

  const fundCategories =
    activeDashboard === "live"
      ? fundComp?.data &&
        Array.isArray(fundComp.data) &&
        fundComp.data.length > 0
        ? fundComp.data.map(
            (f) => f.investment_categories || f.category || "Other",
          )
        : [
            "Renewable Energy Project",
            "Energy Efficiency Initiative",
            "Sustainable Transportation",
            "Waste Management",
            "Water Conservation & Management",
            "Green Building & Infrastructure",
            "Carbon Offsetting",
          ]
      : [
          "Renewable Energy Project",
          "Energy Efficiency Initiative",
          "Sustainable Transportation",
          "Waste Management",
          "Water Conservation & Management",
          "Green Building & Infrastructure",
          "Carbon Offsetting",
        ];

  const fundData =
    activeDashboard === "live"
      ? fundComp?.data &&
        Array.isArray(fundComp.data) &&
        fundComp.data.length > 0
        ? fundComp.data.map((f) => {
            if (f.percentage !== undefined) return f.percentage;
            const targetedVal =
              f.targeted_fund !== undefined
                ? f.targeted_fund
                : f.targeted !== undefined
                  ? f.targeted
                  : 0;
            const achievedVal =
              f.achieved_fund !== undefined
                ? f.achieved_fund
                : f.achieved !== undefined
                  ? f.achieved
                  : 0;
            return targetedVal > 0
              ? Math.round((achievedVal / targetedVal) * 100)
              : 0;
          })
        : [0, 0, 0, 0, 0, 0, 0]
      : [50, 50, 20, 50, 50, 90, 75];

  const dummyDetails = [
    { value: 10, percent: 50 },
    { value: 8, percent: 50 },
    { value: 5, percent: 20 },
    { value: 6, percent: 50 },
    { value: 4, percent: 50 },
    { value: 10, percent: 90 },
    { value: 4, percent: 75 },
  ];

  const fundDetails =
    activeDashboard === "live"
      ? fundComp?.data &&
        Array.isArray(fundComp.data) &&
        fundComp.data.length > 0
        ? fundComp.data.map((f) => {
            const targetedVal =
              f.targeted_fund !== undefined
                ? f.targeted_fund
                : f.targeted !== undefined
                  ? f.targeted
                  : 0;
            const achievedVal =
              f.achieved_fund !== undefined
                ? f.achieved_fund
                : f.achieved !== undefined
                  ? f.achieved
                  : 0;
            const pct =
              f.percentage !== undefined
                ? f.percentage
                : targetedVal > 0
                  ? ((achievedVal / targetedVal) * 100).toFixed(1)
                  : 0;
            return {
              value: targetedVal,
              percent: pct,
            };
          })
        : [
            { value: 0, percent: 0 },
            { value: 0, percent: 0 },
            { value: 0, percent: 0 },
            { value: 0, percent: 0 },
            { value: 0, percent: 0 },
            { value: 0, percent: 0 },
            { value: 0, percent: 0 },
          ]
      : dummyDetails;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-[#F5F5F5]">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[#458276]"></div>
      </div>
    );
  }

  return (
    <div className="md:p-6 lg:p-0 bg-[#F5F5F5] mb-20 sm:mb-6 md:mb-6">
      {!isPermissionCompleted && showPermission && (
        <PermissionForm permissionConClose={() => setPermission(false)} />
      )}
      <div className="mb-2">
        <TimeFilterBar
          title=" Environmental Impact"
          showBreadcrumb={true}
          breadcrumbItems={["Home", "Environmental Impact"]}
          showBackButton={true}
          onBackClick={() => navigate(-1)}
          activeDashboard={activeDashboard}
          onDashboardChange={setActiveDashboard}
          onYearChange={setYearFilter}
          selectedYearValue={yearFilter}
        />
      </div>

      {/* Cards Section */}
      <div
        className={` ${
          !isPermissionCompleted ? "blur-sm opacity-20 pointer-events-none" : ""
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 justify-center">
          <div className="w-full sm:w-[48%] lg:w-[32%]">
            <CarbonCard
              icon={<Env1 />}
              title="Trees Lost"
              value={treesDisplay}
              description={`Total emissions are equivalent to ${treesDisplay} number of trees lost`}
              descriptionBgColor={THEME_COLORS.green}
              descriptionClassName="text-left"
            />
          </div>
          <div className="w-full sm:w-[48%] lg:w-[32%]">
            <CarbonCard
              icon={<Env2 />}
              title="Coal Burnt"
              value={coalDisplay}
              description={`Total emissions are equivalent to ${coalDisplay} metric tons of coal burnt`}
              descriptionBgColor={THEME_COLORS.green}
              descriptionClassName="text-left"
            />
          </div>
          <div className="w-full sm:w-[48%] lg:w-[32%]">
            <CarbonCard
              icon={<Env3 />}
              title="Ice Melt"
              value={iceDisplay}
              description={`Total emissions are equivalent to ${iceDisplay} square meter of ice melts`}
              descriptionBgColor={THEME_COLORS.green}
              descriptionClassName="text-left"
            />
          </div>
        </div>

        {/* Water Footprint Section - Fixed Layout and Responsiveness */}
        <div className="mt-6 w-full bg-white border border-[#3da3baa9] rounded-lg p-4">
          {/* Water Footprint and Activity Charts */}
          <div className="flex flex-col xl:flex-row gap-4">
            {/* Water Footprint Chart */}
            <div className="flex-1 min-w-0 bg-white rounded-lg">
              <div className="px-4 pb-2">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 shrink-0">
                    <Waterfootprint className="w-full h-full" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold font-calibri text-[#06513D]">
                      Water Footprint
                    </h3>
                    <div className="flex gap-4">
                      <p className="text-sm text-gray-600">
                        Total Water Consumption
                      </p>
                      <span className="font-semibold text-gray-800 mt-1">
                        {waterTotalDisplay}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="h-[360px] w-full">
                  <VerticalBarChart
                    categories={[
                      "Surface water",
                      "Groundwater",
                      "Third party water",
                      "Seawater/desalinated water",
                    ]}
                    series={[
                      {
                        name: "Water Consumption",
                        data: waterChartData,
                        originalValues: rawWaterData,
                      },
                    ]}
                    legend="Water Consumption"
                    showLegend={true}
                    legendPosition="right"
                    xAxisTitle="Water Sources"
                    yAxisTitle="water consumption in kilolitres"
                    max={allWaterZero ? 100 : undefined}
                    showPercentage={false}
                  />
                </div>
              </div>
            </div>

            {/* Water Use by Activity */}
            <div className="flex-1 min-w-0 bg-white border border-[#3da3baa9] rounded-lg shadow-sm">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-bold font-calibri text-[#06513D]">
                  Water Use By Activity
                </h3>
              </div>
              <div
                className={`p-4 ${
                  !isPermissionCompleted
                    ? "blur-sm opacity-60 pointer-events-none"
                    : ""
                }`}
              >
                <div className="h-[300px] w-full flex items-center justify-center">
                  <SimplePieChart
                    data={activityData}
                    labels={activityLabels}
                    colors={[
                      THEME_COLORS.yellow,
                      THEME_COLORS.green,
                      THEME_COLORS.darkblue,
                      THEME_COLORS.pink,
                    ]}
                    legendPosition="right"
                    showDataLabels={true}
                    dataLabelsOutside={true}
                    options={{
                      tooltip: {
                        y: {
                          formatter: (val, { seriesIndex }) =>
                            activityData &&
                            activityData[seriesIndex] !== undefined
                              ? `${activityData[seriesIndex]} KL`
                              : `${val} KL`,
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Comparison and Water Intensity */}
          <div className="flex flex-col lg:flex-row gap-4 mt-4">
            {/* Water Usage Comparison */}
            <div className="flex-1 min-w-0 bg-white border border-[#3da3baa9] rounded-lg shadow-sm">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-bold font-calibri text-[#06513D]">
                  Comparison Of Water Usage
                </h3>
              </div>
              <div
                className={`p-4 ${
                  !isPermissionCompleted
                    ? "blur-sm opacity-60 pointer-events-none"
                    : ""
                }`}
              >
                <div className="min-h-[150px] flex items-center justify-center">
                  <ProgressCharts
                    mainLabel="Water usage per employee per hour"
                    mainValue={comparisonValue}
                    totalValue={100}
                    primaryColor={THEME_COLORS.green}
                    secondaryColor="#E7EBEC"
                    primaryLegendLabel={comparisonPrimaryLegend}
                    secondaryLegendLabel={comparisonSecondaryLegend}
                    legendPosition="right"
                  />
                </div>
              </div>
            </div>

            {/* Water Intensity */}
            <div className="flex-1 min-w-0 bg-white border border-[#3da3baa9] rounded-lg shadow-sm">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-bold font-calibri text-[#06513D]">
                  Water Intensity
                </h3>
              </div>
              <div className="p-4">
                <div className="min-h-[150px] flex items-center justify-center">
                  <InfoCard title="Water Intensity" items={intensityItems} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Waste Management Section - Fixed Layout and Responsiveness */}
        <div className="mt-6 w-full">
          <div className="flex flex-col xl:flex-row gap-4">
            {/* Waste Disposal */}
            <div className="bg-white border border-[#3da3baa9] rounded-lg shadow-sm flex-1 w-full overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 shrink-0">
                    <Waste className="w-full h-full" />
                  </div>
                  <div className="flex-1">
                    <h3
                      className="text-lg font-bold font-calibri"
                      style={{ color: THEME_COLORS.green }}
                    >
                      Waste Disposal At An Authorized Center
                    </h3>
                  </div>
                </div>
              </div>
              <div
                className={`px-4 pb-4 ${
                  !isPermissionCompleted
                    ? "blur-sm opacity-60 pointer-events-none"
                    : ""
                }`}
              >
                <TwoColumnBarGraph
                  categories={wasteCategories}
                  seriesData={[
                    { name: "Generated", data: wasteGeneratedData },
                    { name: "Recycled", data: wasteRecycledData },
                  ]}
                  yAxisLeftConfig={{
                    min: 0,
                    max: calculatedWasteMax,
                    tickAmount: 10,
                  }}
                  yAxisRightConfig={{ min: 0, max: 100, tickAmount: 10 }}
                  colors={[THEME_COLORS.green, THEME_COLORS.yellow]}
                  xAxisLabel="Waste Categories"
                  yAxisLabel="Waste (in Mt)"
                  // overlap fix ke saath full number dikhane ke liye:
                  columnWidth="75%" // bars ko chauda kiya taaki number fit ho sake
                  dataLabelsFontSize="8px" // thoda chhota font taaki "1,00,000" na tute
                  gridPaddingTop={30}
                  dataLabelsFormatter={(val) => {
                    if (val === null || val === undefined) return "";
                    return parseFloat(val.toFixed(2)).toLocaleString("en-IN"); // 1,00,000 format
                  }}
                  xAxisBorderOffsetY={-3}
                />
              </div>
            </div>

            {/* Recyclable */}
            <div className="bg-white border border-[#3da3baa9] rounded-lg shadow-sm flex-1 w-full overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 shrink-0">
                    <EmployeeTurnoverRateImage className="w-full h-full" />
                  </div>
                  <div className="flex-1">
                    <h3
                      className="text-lg font-bold font-calibri"
                      style={{ color: THEME_COLORS.green }}
                    >
                      Recyclable
                    </h3>
                  </div>
                </div>
              </div>
              <div
                className={`px-4 pb-4 ${
                  !isPermissionCompleted
                    ? "blur-sm opacity-60 pointer-events-none"
                    : ""
                }`}
              >
                <HorizontalBarCharts
                  categories={[
                    "Total recyclable waste",
                    "Total waste sent for recycling",
                  ]}
                  data={recyclableData}
                  valueLabel="Weight (Kg)"
                  percentLabel="Percent"
                  tooltipUnit="%"
                  colors={[THEME_COLORS.green, THEME_COLORS.pink]}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Environmental Fund Section */}
        <div className="mt-3 mb-20 sm:mb-14 md:mb-8 border border-[#3da3baa9] rounded-lg">
          <HorizontalBarChart
            icon={<Enviromental1 className="w-16 h-16" />}
            title="Environmental Fund allocation Target vs Achieved"
            title1="Investment Categories"
            categories={fundCategories}
            data={fundData}
            details={fundDetails}
            valueLabel="Target Allotted"
            percentLabel="Target Achieved"
            tooltipUnit="%"
            height={250}
            barHeight="50%"
            xh="Environment Fund In cr"
            yh="Investment Categories"
          />
        </div>
      </div>
    </div>
  );
};

export default EmissionDashboard;
